import { LegendVM, RibbonTier, RibbonVM } from "./ribbonTypes";
import { LEGEND_SECTION, METRIC_SECTIONS } from "./leaderboardModel";

export const LEADERBOARD_RIBBON_TAPPED = "LeaderboardRibbonTapped";

/** What the result page knows about the match and ground, shared by every ribbon on it. */
export interface LeaderboardTapContext {
    venueId?: string;
    venueName?: string | null;
    matchId?: string;
    matchEpoch?: number | null;
}

type LeaderboardPeriod = "allTime" | "rollingMonth" | "rollingWindow";
type ComparedAgainst = "venueBoard" | "personalHistory";

const MS_PER_DAY = 24 * 60 * 60 * 1000;

const TIER_PERIOD: Record<RibbonTier, LeaderboardPeriod | undefined> = {
    firstHere: "allTime",
    allTimeRecord: "allTime",
    matched: "allTime",
    monthlyFirst: "rollingMonth",
    onBoard: "rollingMonth",
    personalBest: undefined,
    personalRank: undefined,
    chasing: undefined,
};

/**
 * Values go to Mixpanel in fixed units — km, km/h, a count — whatever the viewer's imperial
 * setting, so a report can aggregate across users.
 */
function reportedValue(ribbon: RibbonVM, stored: number): number {
    switch (ribbon.metric) {
        case "speedMax":
            return Math.round(stored * 3.6 * 10) / 10;
        case "distanceTotal":
            return Math.round(stored / 10) / 100;
        case "sprintsTotal":
            return stored;
    }
}

const VALUE_UNITS = {
    speedMax: "km/h",
    distanceTotal: "km",
    sprintsTotal: "count",
};

function withoutUndefined<T extends Record<string, unknown>>(props: T): T {
    const result: Record<string, unknown> = {};
    Object.keys(props).forEach((key) => {
        if (props[key] !== undefined) {
            result[key] = props[key];
        }
    });
    return result as T;
}

function contextProps(context: LeaderboardTapContext, now: number) {
    return {
        venueId: context.venueId,
        venueName: context.venueName || undefined,
        matchId: context.matchId,
        matchAgeDays:
            context.matchEpoch != null
                ? Math.max(
                      Math.floor((now - context.matchEpoch) / MS_PER_DAY),
                      0
                  )
                : undefined,
    };
}

export function ribbonTapProps(
    ribbon: RibbonVM,
    context: LeaderboardTapContext,
    useImperial: boolean,
    now: number = Date.now()
) {
    const personal =
        ribbon.tier === "personalBest" || ribbon.tier === "personalRank";
    const leaderboardPeriod =
        ribbon.tier === "chasing"
            ? ribbon.chaseTarget === "allTimeRecord"
                ? "allTime"
                : "rollingMonth"
            : TIER_PERIOD[ribbon.tier];

    return withoutUndefined({
        ribbonType: "metric",
        metric: ribbon.metric,
        tier: ribbon.tier,
        leaderboardPeriod,
        comparedAgainst: (personal
            ? "personalHistory"
            : "venueBoard") as ComparedAgainst,
        value: reportedValue(ribbon, ribbon.value),
        valueUnit: VALUE_UNITS[ribbon.metric],
        boardRank: ribbon.boardRank,
        personalRank: ribbon.personalRank,
        gap:
            ribbon.gap !== undefined
                ? reportedValue(ribbon, ribbon.gap)
                : undefined,
        progressPct:
            ribbon.progress !== undefined
                ? Math.round(ribbon.progress * 100)
                : undefined,
        chaseTarget: ribbon.chaseTarget,
        useImperial,
        destinationSection: METRIC_SECTIONS[ribbon.metric],
        ...contextProps(context, now),
    });
}

/** `leaderName` is deliberately absent: it is another player's display name. */
export function legendTapProps(
    legend: LegendVM,
    context: LeaderboardTapContext,
    now: number = Date.now()
) {
    return withoutUndefined({
        ribbonType: "localLegend",
        metric: "matches",
        tier: legend.tier,
        leaderboardPeriod: "rollingWindow" as LeaderboardPeriod,
        comparedAgainst: "venueBoard" as ComparedAgainst,
        windowDays: legend.windowDays,
        boardRank: legend.rank,
        matchesPlayed: legend.matchesPlayed,
        matchesToCrown: legend.matchesToCrown,
        matchesToTop3: legend.matchesToTop3,
        matchesBehindLeader: legend.matchesBehindLeader,
        destinationSection: LEGEND_SECTION,
        ...contextProps(context, now),
    });
}

export function headerTapProps(
    context: LeaderboardTapContext,
    now: number = Date.now()
) {
    return withoutUndefined({
        ribbonType: "venueHeader",
        ...contextProps(context, now),
    });
}
